import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';

interface BidNotification {
  id: string;
  productoId: string;
  productoTitulo: string;
  montoNuevo: number;
  visible: boolean;
}

export default function BidNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState<BidNotification[]>([]);

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, visible: false } : n))
    );
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 400);
  }, []);

  useEffect(() => {
    if (!user) return;

    // Suscripción realtime a nuevas pujas en productos donde el usuario tiene pujas activas
    const channel = supabase
      .channel('bid-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'bids',
        },
        async (payload) => {
          const newBid = payload.new as { id: string; producto_id: string; pujador_id: string; monto: number };

          // No notificar si la puja es del mismo usuario
          if (newBid.pujador_id === user.id) return;

          // Verificar si el usuario tiene una puja en este producto
          const { data: miPuja } = await supabase
            .from('bids')
            .select('id')
            .eq('producto_id', newBid.producto_id)
            .eq('pujador_id', user.id)
            .maybeSingle();

          if (!miPuja) return;

          // Obtener info del producto
          const { data: producto } = await supabase
            .from('products')
            .select('id, titulo')
            .eq('id', newBid.producto_id)
            .maybeSingle();

          if (!producto) return;

          const notifId = `${newBid.id}-${Date.now()}`;
          const newNotif: BidNotification = {
            id: notifId,
            productoId: producto.id,
            productoTitulo: producto.titulo,
            montoNuevo: newBid.monto,
            visible: true,
          };

          setNotifications((prev) => [newNotif, ...prev].slice(0, 3));

          // Auto-dismiss después de 8 segundos
          setTimeout(() => removeNotification(notifId), 8000);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user, removeNotification]);

  if (notifications.length === 0) return null;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3 max-w-sm w-full">
      {notifications.map((notif) => (
        <div
          key={notif.id}
          className={`bg-white rounded-2xl p-4 border border-red-200 transition-all duration-400 ${
            notif.visible
              ? 'opacity-100 translate-y-0'
              : 'opacity-0 translate-y-4 pointer-events-none'
          }`}
          style={{ boxShadow: '0 4px 24px rgba(239,68,68,0.15)' }}
        >
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 flex items-center justify-center bg-red-100 rounded-xl shrink-0 animate-pulse">
              <i className="ri-auction-line text-red-600 text-lg"></i>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-gray-900">¡Te han superado la puja!</p>
              <p className="text-xs text-gray-600 mt-0.5 line-clamp-1">{notif.productoTitulo}</p>
              <p className="text-sm font-semibold text-red-600 mt-1">
                Nueva puja: ${notif.montoNuevo.toFixed(2)}
              </p>
            </div>
            <button
              onClick={() => removeNotification(notif.id)}
              className="text-gray-400 hover:text-gray-600 cursor-pointer shrink-0 mt-0.5"
            >
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
          <button
            onClick={() => {
              navigate(`/producto/${notif.productoId}`);
              removeNotification(notif.id);
            }}
            className="mt-3 w-full py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-arrow-right-line mr-1"></i>Volver a pujar ahora
          </button>
        </div>
      ))}
    </div>
  );
}
